import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Loading, ErrorBox, Empty } from "@/components/common";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

export default function Logs() {
  const { tx } = useI18n();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [op, setOp] = useState("");
  const [actor, setActor] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => {
    const t = setTimeout(() => api.get("/logs", { params: { operation: op || undefined, actor: actor || undefined, page } }).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))), 250);
    return () => clearTimeout(t);
  }, [op, actor, page]);
  const verify = async () => {
    const { data } = await api.get("/admin/logs/verify");
    data.valid ? toast.success(`${tx("Chaîne intègre", "Chain intact")} · ${data.checked}`) : toast.error(`${tx("Rupture à", "Broken at")} #${data.broken_at_seq}`);
  };
  if (err) return <ErrorBox error={err} />;
  return (
    <div>
      <PageHeader eyebrow={tx("Traçabilité", "Traceability")} title={tx("Journaux d'accès et d'audit", "Access & audit logs")}
        subtitle={tx("Journal en ajout seul, chaîné par hachage SHA-256. Les chefs de mission ne voient que leurs missions.", "Append-only log, SHA-256 hash-chained. Mission leads only see their missions.")}
        actions={user.platform_role === "admin" && <Button variant="outline" data-testid="verify-chain-btn" onClick={verify}><ShieldCheck className="h-4 w-4 mr-1" />{tx("Vérifier l'intégrité", "Verify integrity")}</Button>} />
      <div className="flex flex-wrap gap-3 mb-4">
        <Input data-testid="logs-operation-filter" className="w-56" placeholder={tx("Opération (ex. member_revoked)", "Operation (e.g. member_revoked)")} value={op} onChange={(e) => { setOp(e.target.value); setPage(1); }} />
        <Input data-testid="logs-actor-filter" className="w-56" placeholder={tx("Acteur (e-mail)", "Actor (email)")} value={actor} onChange={(e) => { setActor(e.target.value); setPage(1); }} />
      </div>
      {!d ? <Loading /> : d.items.length === 0 ? <div className="card-surface"><Empty text="—" /></div> : (
        <div className="card-surface overflow-x-auto">
          <table className="tbl text-xs" data-testid="logs-table">
            <thead><tr><th>#</th><th>{tx("Horodatage", "Timestamp")}</th><th>{tx("Acteur", "Actor")}</th><th>{tx("Opération", "Operation")}</th><th>{tx("Objet", "Object")}</th><th>{tx("Détails", "Details")}</th><th>IP</th><th>Hash</th></tr></thead>
            <tbody>{d.items.map((l) => (
              <tr key={l.id} data-testid={`log-row-${l.seq}`}><td className="font-mono">{l.seq}</td><td className="whitespace-nowrap">{fmtDate(l.timestamp)}</td><td>{l.actor_email || "—"}</td><td className="font-mono font-medium text-[#153A63]">{l.operation}</td>
                <td className="font-mono">{l.object_type}{l.object_id && `:${l.object_id.slice(0, 8)}`}</td><td className="max-w-xs truncate" title={JSON.stringify(l.details)}>{JSON.stringify(l.details)}</td><td>{l.ip}</td><td className="font-mono text-slate-400">{l.hash.slice(0, 10)}</td></tr>))}
            </tbody>
          </table>
          <div className="flex justify-end gap-2 p-3 text-sm"><Button size="sm" variant="outline" data-testid="logs-prev" disabled={page === 1} onClick={() => setPage(page - 1)}>‹</Button><span className="py-1">{page} / {Math.max(1, Math.ceil(d.total / 50))}</span><Button size="sm" variant="outline" data-testid="logs-next" disabled={page * 50 >= d.total} onClick={() => setPage(page + 1)}>›</Button></div>
        </div>
      )}
    </div>
  );
}
